import { useCallback, useEffect, useRef, useState } from "react";
import { getCurrentLocale } from "../../../../../utlis/locale";

const SPEECH_LOCALES = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  ja: "ja-JP",
  ru: "ru-RU",
  zh: "zh-CN",
};

const getVoiceErrorKey = (error) => {
  const value = String(error || "").toLowerCase();
  if (value === "aborted") return null;
  if (
    value.includes("not-allowed") ||
    value.includes("not allowed") ||
    value.includes("denied") ||
    value.includes("permission")
  ) {
    return "voice_permission_denied";
  }
  if (value.includes("no-speech") || value.includes("no speech")) {
    return "voice_no_speech";
  }
  if (value.includes("audio-capture") || value.includes("microphone")) {
    return "voice_microphone_unavailable";
  }
  if (value.includes("unavailable") || value.includes("not supported")) {
    return "voice_not_supported";
  }
  return "voice_failed";
};

/**
 * Speech-to-text, shared by the note thread and the report's editable
 * recommendation. Calls `onTranscript(text)` with the transcript; the caller
 * decides how to apply it (append, replace, …).
 *
 * Voice transcription is client-side because the Action Plan API does not
 * expose a speech-to-text endpoint.
 */
const useVoiceInput = (onTranscript) => {
  const [listening, setListening] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const recognitionRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);
  const deliveredTranscriptsRef = useRef(new Set());

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  useEffect(
    () => () => {
      const recognition = recognitionRef.current;
      recognitionRef.current = null;
      if (recognition) {
        recognition.onend = null;
        recognition.abort?.();
      }
    },
    [],
  );

  const start = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setErrorKey("voice_not_supported");
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    deliveredTranscriptsRef.current = new Set();
    setErrorKey(null);
    recognition.lang = SPEECH_LOCALES[getCurrentLocale()] || "en-US";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => {
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }
      setListening(false);
    };
    recognition.onerror = (event) => {
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }
      setListening(false);
      setErrorKey(getVoiceErrorKey(event?.error));
    };
    recognition.onresult = (event) => {
      const results = event.results || [];
      const firstResult = Number.isInteger(event.resultIndex)
        ? event.resultIndex
        : 0;

      for (let index = firstResult; index < results.length; index += 1) {
        const result = results[index];
        if (result?.isFinal === false) continue;
        const transcript = result?.[0]?.transcript?.trim();
        if (!transcript || deliveredTranscriptsRef.current.has(transcript)) {
          continue;
        }
        deliveredTranscriptsRef.current.add(transcript);
        onTranscriptRef.current?.(transcript);
      }
    };
    try {
      recognition.start();
    } catch (error) {
      recognitionRef.current = null;
      setListening(false);
      setErrorKey(getVoiceErrorKey(error?.message));
    }
  }, []);

  return { listening, errorKey, start };
};

export default useVoiceInput;
